import React from "react";

import { ucFirst } from "./names";
/**
 * @typedef {{uuid: string, name: string, repetition: Boolean, tags: string[], parents: (string|null)[]}} CompetenceProfile
 * @typedef {{competence_profile: CompetenceProfile}} Certification
 * @typedef {{competence_profile: CompetenceProfile, children: CertificationNode[]}} CertificationNode
 */

/**
 * Rendering of available certifications:
 * 1. Build a tree:
 *    buildCertificationTree(available_certifications)
 * 2. Filter out unrelated certifications:
 *    filterUnrelatedCertifications(current_profile, tree)
 * 3. Recursively render the resulting tree.
 */

/**
 * Checks if the given node is (also) a root node.
 * Because it's possible for a node to be both a child and a root node, we add a null
 * value to the list of parents to indicate that this node is (also) a root node.
 * @param {CertificationNode} node
 */
export const isRootCertificationNode = (node) => {
    const { parents } = node.competence_profile;
    return parents.length === 0 || parents.includes(null);
};

/**
 * Returns whether this node is (also) a child node.
 * @param {CertificationNode} node
 * @returns {boolean}
 */
export const isChildCertificationNode = (node) => {
    const { parents } = node.competence_profile;
    return parents.length > 0 && !parents.every(parent => parent === null);
};

/**
 * The profiles in the available certifications can be under multiple parents in the
 * tree. This function will nest each profile under all their parent and return a
 * list of root nodes.
 * @param {Certification[]} certifications
 * @returns {CertificationNode[]}
 */
export const buildCertificationTree = (certifications, deduplicate = false) => {
    // map the profiles to their competence profile UUID:
    const map = certifications.reduce((map, certification) => {
        map[certification.competence_profile.uuid] = certification;
        certification.children = [];
        return map;
    }, {});

    // add children to each profile:
    certifications.forEach((certification) => {
        certification.competence_profile.parents.forEach((parentUUID) => {
            if (!map[parentUUID]) {
                // indication that this node is also a root node
                return;
            }
            map[parentUUID]?.children.push(certification);
        });
    });

    // return the root nodes:
    const tree = certifications.filter(isRootCertificationNode);
    const setDepth = (nodes, depth = 1) => nodes.map((node) => (
        {...node, depth, children: setDepth(node.children, depth + 1)}
    ));
    const dedupe = (nodes, known = new Set()) => {
        const deduped = nodes.filter((node) => {
            if (known.has(node.competence_profile.uuid)) {
                return false;
            }
            known.add(node.competence_profile.uuid);
            return true;
        });
        return deduped.map((node) => ({
            ...node,
            children: dedupe(node.children, known)
        }));
    };
    return setDepth(deduplicate ? dedupe(tree) : tree);
};

/**
 * Removes all siblings of the given profile from the list of nodes.
 * @param {Certification} profile
 * @param {CertificationNode[]} nodes
 * @returns {CertificationNode[]}
 */
export const removeSiblingsOfProfile = (profile, nodes) => {
    const isCurrentProfile = (node) => node.competence_profile.uuid === profile.uuid;
    if (nodes.some(isCurrentProfile)) {
        // if the current profile is in the list, we remove all siblings:
        return nodes.filter(isCurrentProfile);
        // we want to keep all children of the current profile:
    }
    // if the current profile is not in this list, we descend into the children:
    return nodes.map((node) => {
        if (node.children) {
            node.children = removeSiblingsOfProfile(profile, node.children);
        }
        return node;
    });
};

/**
 * Checks if two profiles are related to each other
 * @param {CompetenceProfile} currentProfile
 * @param {CompetenceProfile} otherProfile
 * @returns {Boolean}
 */
export const profilesAreRelated = (currentProfile, otherProfile) => {
    // Johan: Server now filters out "variants" of the same profile and only returns
    // the "main" profiles. So we can assume that it's always related:
    // return true;
    // Johan: We simply use the "related" property given by the server.
    // NIBHV-438 - Temporarily use the related property from the server until the
    // variants functionality is released.
    // return otherProfile.related;
    if (APP_CONFIG.filter_related_profiles === "server") {
        return otherProfile.related;
    }
    return true;
    /*
    // console.info(currentProfile.name, " vs ", otherProfile.name);
    if (!currentProfile || !otherProfile) return false;
    // console.info(currentProfile.name, " vs ", otherProfile.name);
    if (currentProfile.uuid === otherProfile.uuid) {
        // console.info("same profile");
        // same profile
        return true;
    }
    if (["repetition", "language"].some((key) => currentProfile[key] !== otherProfile[key])) {
        // if any of these fields are not the same, we are not related:
        // console.info("not same rep or lang");
        return false;
    }
    // console.info("tags", currentProfile.tags, otherProfile.tags);
    // now we only have tags to compare.
    return currentProfile.tags.length === otherProfile.tags.length && currentProfile.tags.every((tag) => otherProfile.tags.includes(tag));
    */
};
/**
 * Takes an Array of certifications and filters out all certifications that are not related to the current profile.
 * @param {CompetenceProfile} currentProfile
 * @param {Certification[]} certifications
 * @returns {Certification[]}
 */
export const filterUnrelatedCertifications = (currentProfile, certifications) => {
    return certifications.filter((certification) => {
        return profilesAreRelated(currentProfile, certification.competence_profile);
        // we don't check the children anymore.
        // If a parent is unrelated, we consider all children unrelated as well.
        // || filterUnrelatedCertifications(currentProfile, certification.children).length > 0;
    });
};

/**
 * Return the name of a certificate type
 * @param type
 * @returns {*}
 */
export function certificateTypeName(type) {
    const names = {
        certificate: "certificaat",
        diploma: "diploma"
    };
    return names[type] || type;
}

/**
 * Certificate type component
 * @param type
 * @returns {JSX.Element}
 */
export function CertificateType({ type }) {
    return <span className={`certificate-type certificate-type--${type}`}>{ucFirst(certificateTypeName(type))}</span>;
}
